// SolarInstallationDoorLockService.js - Detailed Door Lock Systems service component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a detailed Door Lock Systems service section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the service section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The Door Lock service section elements
 */
export const createSolarInstallationDoorLockServiceElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const doorLockSectionClass = 'door-lock-service-section';
  const doorLockContainerClass = 'door-lock-service-container';
  const doorLockHeaderClass = 'door-lock-service-header';
  const doorLockTitleClass = 'door-lock-service-title';
  const doorLockSubtitleClass = 'door-lock-service-subtitle';
  const doorLockContentClass = 'door-lock-service-content';
  const doorLockHeroClass = 'door-lock-service-hero';
  const doorLockHeroImageClass = 'door-lock-service-hero-image';
  const doorLockHeroTextClass = 'door-lock-service-hero-text';
  const doorLockFeaturesClass = 'door-lock-service-features';
  const doorLockFeatureCardClass = 'door-lock-service-feature-card';
  const doorLockFeatureIconClass = 'door-lock-service-feature-icon';
  const doorLockFeatureTitleClass = 'door-lock-service-feature-title';
  const doorLockFeatureDescClass = 'door-lock-service-feature-desc';
  const doorLockPackagesClass = 'door-lock-service-packages';
  const doorLockPackageCardClass = 'door-lock-service-package-card';
  const doorLockPackageTitleClass = 'door-lock-service-package-title';
  const doorLockPackagePriceClass = 'door-lock-service-package-price';
  const doorLockPackageListClass = 'door-lock-service-package-list';
  const doorLockPackageItemClass = 'door-lock-service-package-item';
  const doorLockCtaClass = 'door-lock-service-cta';
  const doorLockCtaButtonClass = 'door-lock-service-cta-button';
  
  // Desktop Styles - Solar themed with deep blue background
  const doorLockSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 50%, #0f2341 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const doorLockContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const doorLockHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  };

  const doorLockTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const doorLockSubtitleStyles = {
    fontSize: '20px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    maxWidth: '700px'
  };

  const doorLockContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '80px'
  };

  const doorLockHeroStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '50px',
    width: '100%'
  };

  const doorLockHeroImageStyles = {
    flex: '1',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
  };

  const doorLockHeroTextStyles = {
    flex: '1',
    color: '#ffffff',
    fontSize: '18px',
    lineHeight: '1.8',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockFeaturesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const doorLockFeatureCardStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '30px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const doorLockFeatureIconStyles = {
    fontSize: '48px',
    marginBottom: '20px',
    display: 'block'
  };

  const doorLockFeatureTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#4ade80',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockFeatureDescStyles = {
    fontSize: '16px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockPackagesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const doorLockPackageCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    border: '2px solid #4ade80',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease'
  };

  const doorLockPackageTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '10px',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockPackagePriceStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#4ade80',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockPackageListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const doorLockPackageItemStyles = {
    fontSize: '16px',
    color: '#475569',
    marginBottom: '12px',
    paddingLeft: '25px',
    position: 'relative',
    fontFamily: '"Inter", sans-serif'
  };

  const doorLockCtaStyles = {
    textAlign: 'center',
    width: '100%'
  };

  const doorLockCtaButtonStyles = {
    padding: '18px 40px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)'
  };

  // Mobile styles
  const mobileDoorLockHeroStyles = {
    flexDirection: 'column',
    gap: '30px'
  };

  const mobileDoorLockTitleStyles = {
    fontSize: '36px'
  };

  const mobileDoorLockPackageCardStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(doorLockSectionClass, doorLockSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockContainerClass, doorLockContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockHeaderClass, doorLockHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockTitleClass, doorLockTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockSubtitleClass, doorLockSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockContentClass, doorLockContentStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockHeroClass, doorLockHeroStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockHeroImageClass, doorLockHeroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockHeroTextClass, doorLockHeroTextStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockFeaturesClass, doorLockFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockFeatureCardClass, doorLockFeatureCardStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockFeatureIconClass, doorLockFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockFeatureTitleClass, doorLockFeatureTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockFeatureDescClass, doorLockFeatureDescStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackagesClass, doorLockPackagesStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackageCardClass, doorLockPackageCardStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackageTitleClass, doorLockPackageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackagePriceClass, doorLockPackagePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackageListClass, doorLockPackageListStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockPackageItemClass, doorLockPackageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockCtaClass, doorLockCtaStyles);
    newRegistry[DEVICES.DESKTOP].set(doorLockCtaButtonClass, doorLockCtaButtonStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(doorLockHeroClass, mobileDoorLockHeroStyles);
    newRegistry[DEVICES.MOBILE].set(doorLockTitleClass, mobileDoorLockTitleStyles);
    newRegistry[DEVICES.MOBILE].set(doorLockPackageCardClass, mobileDoorLockPackageCardStyles);

    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    doorLockSectionClass,
    doorLockContainerClass,
    doorLockHeaderClass,
    doorLockTitleClass,
    doorLockSubtitleClass,
    doorLockContentClass,
    doorLockHeroClass,
    doorLockHeroImageClass,
    doorLockHeroTextClass,
    doorLockFeaturesClass,
    doorLockFeatureCardClass,
    doorLockFeatureIconClass,
    doorLockFeatureTitleClass,
    doorLockFeatureDescClass,
    doorLockPackagesClass,
    doorLockPackageCardClass,
    doorLockPackageTitleClass,
    doorLockPackagePriceClass,
    doorLockPackageListClass,
    doorLockPackageItemClass,
    doorLockCtaClass,
    doorLockCtaButtonClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const heroId = sectionId + 6;
  const heroImageId = sectionId + 7;
  const heroTextId = sectionId + 8;
  const featuresId = sectionId + 9;
  const packagesId = sectionId + 20;
  const ctaId = sectionId + 30;
  const ctaButtonId = sectionId + 31;

  const newElements = [
    // Main Door Lock Service Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Door Lock Service Section',
      parentId: null,
      className: doorLockSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-section',
        'data-component': 'section',
        'data-section-type': 'service',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Door Lock Service Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: doorLockContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Door Lock Service Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: doorLockHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Door Lock Service Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: doorLockTitleClass,
      styles: {},
      children: [],
      text: 'Electrical Door Lock Systems',
      attributes: {
        'data-element': 'door-lock-service-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Door Lock Service Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: doorLockSubtitleClass,
      styles: {},
      children: [],
      text: 'Advanced smart access control solutions for modern security needs. Professional installation of electronic door locks, keypad systems, card readers, and biometric access controls.',
      attributes: {
        'data-element': 'door-lock-service-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Door Lock Service Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: doorLockContentClass,
      styles: {},
      children: [heroId, featuresId, packagesId, ctaId],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-content'
      }
    },
    
    // Hero Section
    {
      id: heroId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Door Lock Service Hero',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: doorLockHeroClass,
      styles: {},
      children: [heroImageId, heroTextId],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-hero'
      }
    },
    
    // Hero Image
    {
      id: heroImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Door Lock Service Hero Image',
      parentId: heroId,
      hierarchyName: getHierarchyName('image'),
      className: doorLockHeroImageClass,
      styles: { width: '100%', height: 'auto' },
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'door-lock-service-hero-image',
        'alt': 'Smart electronic door lock system'
      }
    },
    
    // Hero Text
    {
      id: heroTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Door Lock Service Hero Text',
      parentId: heroId,
      hierarchyName: getHierarchyName('div'),
      className: doorLockHeroTextClass,
      styles: {},
      children: [],
      text: 'JNT & Co. specializes in cutting-edge electrical door lock systems that provide enhanced security and convenience for your property. Our smart access control solutions include keyless entry systems, biometric locks, card readers, and mobile app-controlled locks. We design and install customized access control systems for residential homes, commercial buildings, and industrial facilities. Our expert technicians ensure seamless integration with your existing security infrastructure while providing user-friendly operation and reliable performance.',
      attributes: {
        'data-element': 'door-lock-service-hero-text'
      }
    }
  ];

  return {
    doorLockServiceSection: newElements[0],
    elements: newElements
  };
}; 